import type { ColumnDef, ColumnSizingState } from '@tanstack/react-table'
import type { Lead } from '@/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'
import { Checkbox } from '@/components/ui/checkbox'
import { DealHealthBadge } from '@/components/DealHealthScore'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Info, Building2 } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import { CATEGORICAL, SEMANTIC, GRACKLE_COLORS } from '@/lib/theme'

/**
 * Lead status → color mapping (WCAG AA compliant).
 * Status badges use .tint fill with Prussian text.
 */
const LEAD_STATUS_COLORS = {
  New: CATEGORICAL.powder,
  Working: CATEGORICAL.teal,
  Nurturing: CATEGORICAL.steel,
  Qualified: SEMANTIC.success,
  Unqualified: CATEGORICAL.slate,
  Converted: CATEGORICAL.evergreen,
  Disqualified: SEMANTIC.neutral,
} as const

interface LeadsColumnsProps {
  onDeleteLead?: (leadId: string, name: string) => void
  onConvertLead: (lead: Lead) => void
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
  onToggleSelectAll?: () => void
  allSelected?: boolean
}

export const createLeadsColumns = ({
  onDeleteLead,
  onConvertLead,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allSelected,
}: LeadsColumnsProps): ColumnDef<Lead>[] => [
  {
    id: 'select',
    header: () => (
      <Checkbox
        checked={allSelected}
        onCheckedChange={onToggleSelectAll}
        aria-label="Select all leads"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={selectedIds?.has(row.original.id) ?? false}
        onCheckedChange={() => onToggleSelect?.(row.original.id)}
        aria-label={`Select lead ${row.original.name}`}
      />
    ),
    size: 50,
    minSize: 50,
    maxSize: 50,
    enableResizing: false,
  },
  {
    id: 'name',
    header: 'Name',
    accessorKey: 'name',
    meta: { sortField: 'name' },
    size: 200,
    cell: ({ row }) => (
      <div className="flex items-center gap-2 min-w-0">
        <Link
          to={`/leads/${row.original.id}`}
          className="font-medium text-primary hover:underline truncate"
          aria-label={`View details for ${row.original.name}`}
        >
          {row.original.name}
        </Link>
        {row.original.has_task === false && (
          <Badge
            variant="outline"
            className="text-[10px] px-1.5 py-0 whitespace-nowrap"
            style={{
              backgroundColor: CATEGORICAL.raspberry.solid,
              color: '#FFFFFF',
              borderColor: CATEGORICAL.raspberry.solid,
            }}
          >
            No Task
          </Badge>
        )}
        {row.original.matched_account_id && row.original.matched_account_name && (
          <Link
            to={`/accounts/${row.original.matched_account_id}`}
            aria-label={`Matches account: ${row.original.matched_account_name}`}
            title={`Matches account: ${row.original.matched_account_name}`}
            className="shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
          >
            <Badge
              variant="secondary"
              className="gap-1 text-[10px] px-1.5 py-0 max-w-[140px] whitespace-nowrap hover:underline"
            >
              <Building2 className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{row.original.matched_account_name}</span>
            </Badge>
          </Link>
        )}
      </div>
    ),
  },
  {
    id: 'company',
    header: 'Company',
    accessorKey: 'company',
    meta: { sortField: 'company' },
    size: 200,
  },
  {
    id: 'email',
    header: 'Email',
    accessorKey: 'email',
    size: 220,
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    meta: { sortField: 'status' },
    size: 120,
    cell: ({ row }) => {
      if (!row.original.status) return null
      const status = row.original.status as keyof typeof LEAD_STATUS_COLORS
      const colors = LEAD_STATUS_COLORS[status] || CATEGORICAL.slate
      // CATEGORICAL has {solid, tint}, SEMANTIC has {fill, text}
      const bgColor = 'tint' in colors ? colors.tint : colors.fill
      const textColor = 'text' in colors ? colors.text : GRACKLE_COLORS.prussianBlue
      return (
        <Badge
          variant="outline"
          style={{
            backgroundColor: bgColor,
            color: textColor,
            borderColor: bgColor,
          }}
        >
          {row.original.status}
        </Badge>
      )
    },
  },
  {
    id: 'lead_source',
    header: 'Lead Source',
    accessorKey: 'lead_source',
    meta: { sortField: 'lead_source' },
    size: 150,
    cell: ({ row }) => (
      <span className="truncate block max-w-[150px]">{row.original.lead_source || '-'}</span>
    ),
  },
  {
    id: 'marketing_status__c',
    header: 'Marketing Status',
    accessorKey: 'marketing_status__c',
    meta: { sortField: 'marketing_status__c' },
    size: 150,
    cell: ({ row }) => row.original.marketing_status__c || '-',
  },
  {
    id: 'score',
    header: () => (
      <div className="flex items-center gap-1">
        Score
        <Popover>
          <PopoverTrigger asChild>
            <button
              className="inline-flex items-center text-muted-foreground hover:text-foreground"
              aria-label="How is the lead score calculated?"
            >
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-3" align="start">
            <p className="text-xs font-semibold mb-2">Lead Score (0-100)</p>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Engagement</span>
                <span className="font-medium">0-25</span>
              </div>
              <p className="text-muted-foreground text-[11px] -mt-1">
                Recent tasks/events in the last 7-60 days
              </p>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Profile</span>
                <span className="font-medium">0-25</span>
              </div>
              <p className="text-muted-foreground text-[11px] -mt-1">
                Data completeness (email, phone, title, etc.)
              </p>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Lead Source</span>
                <span className="font-medium">0-25</span>
              </div>
              <p className="text-muted-foreground text-[11px] -mt-1">
                Quality tier of the lead source
              </p>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className="font-medium">0-25</span>
              </div>
              <p className="text-muted-foreground text-[11px] -mt-1">Current status and lead age</p>
            </div>
            <div className="border-t mt-2 pt-2 space-y-0.5 text-[11px]">
              <div className="flex justify-between">
                <span style={{ color: CATEGORICAL.evergreen.solid }}>Hot Lead</span>
                <span>80+</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: CATEGORICAL.teal.solid }}>Warm Lead</span>
                <span>60+</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: CATEGORICAL.clay.solid }}>Needs Work</span>
                <span>40+</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: CATEGORICAL.slate.solid }}>Cold Lead</span>
                <span>20+</span>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    ),
    accessorKey: 'lead_score',
    size: 100,
    cell: ({ row }) =>
      row.original.status !== 'Disqualified' ? (
        <DealHealthBadge score={row.original.lead_score} />
      ) : null,
  },
  {
    id: 'owner_name',
    header: 'Owner',
    accessorKey: 'owner_name',
    meta: { sortField: 'owner_name' },
    size: 150,
    cell: ({ row }) => (
      <span className="truncate block max-w-[150px]">
        {(row.original as any).owner_name || '-'}
      </span>
    ),
  },
  {
    id: 'created_date',
    header: 'Created',
    accessorKey: 'created_date',
    meta: { sortField: 'created_date' },
    size: 120,
    cell: ({ row }) => (row.original.created_date ? formatDate(row.original.created_date) : '-'),
  },
  {
    id: 'actions',
    header: () => <div className="text-right">Actions</div>,
    size: 220,
    minSize: 200,
    maxSize: 280,
    enableResizing: false,
    cell: ({ row }) => (
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" aria-label={`View lead ${row.original.name}`} asChild>
          <Link to={`/leads/${row.original.id}`}>View</Link>
        </Button>
        {!row.original.is_converted && (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Convert lead ${row.original.name}`}
            onClick={() => onConvertLead(row.original)}
          >
            Convert
          </Button>
        )}
        {onDeleteLead && (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Delete lead ${row.original.name}`}
            onClick={() => onDeleteLead(row.original.id, row.original.name ?? '')}
          >
            Delete
          </Button>
        )}
      </div>
    ),
  },
]

export const leadsDefaultColumnSizing: ColumnSizingState = {
  select: 50,
  name: 200,
  company: 200,
  email: 220,
  status: 120,
  lead_source: 150,
  marketing_status__c: 150,
  rating: 100,
  score: 100,
  owner_name: 150,
  created_date: 120,
  actions: 220,
}
